import React from 'react'
import { useTranslation } from 'react-i18next'
import { PiggyBank } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cn } from '@/lib/utils'
import { formatMoney, isNegativeMoney } from '../lib/money'
import { provisionalDrawn } from '../lib/provisional'
import type { CostProvisionalSummary } from '../types'

interface Props {
  rows: CostProvisionalSummary[]
  currency: string
  locale: string
  canDraw: (row: CostProvisionalSummary) => boolean
  onDraw: (row: CostProvisionalSummary) => void
}

/** Part D spec §2.3: each provisional line's allowance — původně · čerpáno · zbývá · čeká — and "Čerpat z rezervy". */
const ContractProvisionalCard: React.FC<Props> = ({ rows, currency, locale, canDraw, onDraw }) => {
  const { t } = useTranslation()
  const money = (v: string) => formatMoney(v, { locale, currency })
  return (
    <Card className="border-0 shadow-sm">
      <CardHeader><CardTitle className="text-base">{t('CostControl_provisional_title')}</CardTitle></CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('CostControl_lines_item')}</TableHead>
              <TableHead className="w-36 text-right">{t('CostControl_provisional_col_original')}</TableHead>
              <TableHead className="w-36 text-right">{t('CostControl_provisional_col_drawn')}</TableHead>
              <TableHead className="w-36 text-right">{t('CostControl_provisional_col_remaining')}</TableHead>
              <TableHead className="w-36 text-right">{t('CostControl_provisional_col_pending')}</TableHead>
              <TableHead className="w-48" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.nodeId}>
                <TableCell>
                  {r.path && <div className="text-xs text-muted-foreground">{r.path}</div>}
                  <div>{r.nodeName}</div>
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(r.original)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(provisionalDrawn(r))}</TableCell>
                <TableCell className={cn('text-right font-medium tabular-nums', isNegativeMoney(r.remaining) && 'text-destructive')}>{money(r.remaining)}</TableCell>
                <TableCell className="text-right tabular-nums text-muted-foreground">{money(r.pending)}</TableCell>
                <TableCell className="text-right">
                  {canDraw(r) && (
                    <Button variant="outline" size="sm" onClick={() => onDraw(r)}>
                      <PiggyBank className="h-4 w-4" />
                      {t('CostControl_provisional_draw')}
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  )
}
export default ContractProvisionalCard
